import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { adminApi } from '@/api';
import type { UserProfile, LoginRequest } from '@/shared';

interface AdminUser extends UserProfile {
  role?: string;
  is_super_admin?: boolean;
  permissions?: string[];
}

interface AdminSession {
  access_token: string;
  user: AdminUser;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AdminUser | null>(null);
  const token = ref<string | null>(null);
  const adminRole = ref<string | null>(null);
  const isSuperAdmin = ref(false);
  const permissions = ref<string[]>([]);
  const loading = ref(false);

  const isAuthenticated = computed(() => !!token.value && !!user.value);

  // Check if user has a specific permission
  function hasPermission(permission: string): boolean {
    // Super admin has all permissions
    if (isSuperAdmin.value) {
      return true;
    }
    // Check for wildcard permission (same as backend logic)
    if (permissions.value.includes('*')) {
      return true;
    }
    return permissions.value.includes(permission);
  }

  // Check if user has any of the given permissions
  function hasAnyPermission(perms: string[]): boolean {
    if (isSuperAdmin.value) {
      return true;
    }
    // Check for wildcard permission
    if (permissions.value.includes('*')) {
      return true;
    }
    return perms.some(p => permissions.value.includes(p));
  }

  // Check if user has all of the given permissions
  function hasAllPermissions(perms: string[]): boolean {
    if (isSuperAdmin.value) {
      return true;
    }
    // Check for wildcard permission
    if (permissions.value.includes('*')) {
      return true;
    }
    return perms.every(p => permissions.value.includes(p));
  }

  function initialize() {
    const savedToken = localStorage.getItem('admin_token');
    const savedUser = localStorage.getItem('admin_user');
    const savedRole = localStorage.getItem('admin_role');
    const savedSuperAdmin = localStorage.getItem('admin_is_super_admin');
    const savedPermissions = localStorage.getItem('admin_permissions');

    if (savedToken && savedUser) {
      token.value = savedToken;
      user.value = JSON.parse(savedUser);
      adminRole.value = savedRole;
      isSuperAdmin.value = savedSuperAdmin === 'true';
      permissions.value = savedPermissions ? JSON.parse(savedPermissions) : [];
      adminApi.setToken(savedToken);
    }
  }

  async function login(credentials: LoginRequest) {
    loading.value = true;
    try {
      const response = await adminApi.auth.login(credentials);

      if (response.requires_2fa) {
        return { requires_2fa: true, two_factor_token: response.two_factor_token };
      }

      setSession(response as AdminSession);
      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  async function verify2FA(twoFactorToken: string, code: string) {
    loading.value = true;
    try {
      const response = await adminApi.auth.verify2FA(twoFactorToken, code);
      setSession(response as AdminSession);
      return { success: true };
    } finally {
      loading.value = false;
    }
  }

  function setSession(response: AdminSession) {
    token.value = response.access_token;
    user.value = response.user;
    adminRole.value = response.user.role || null;
    isSuperAdmin.value = response.user.is_super_admin || false;
    permissions.value = response.user.permissions || [];

    localStorage.setItem('admin_token', response.access_token);
    localStorage.setItem('admin_user', JSON.stringify(response.user));
    if (response.user.role) {
      localStorage.setItem('admin_role', response.user.role);
    }
    localStorage.setItem('admin_is_super_admin', String(response.user.is_super_admin || false));
    if (response.user.permissions) {
      localStorage.setItem('admin_permissions', JSON.stringify(response.user.permissions));
    }
    adminApi.setToken(response.access_token);
  }

  function logout() {
    token.value = null;
    user.value = null;
    adminRole.value = null;
    isSuperAdmin.value = false;
    permissions.value = [];
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_role');
    localStorage.removeItem('admin_is_super_admin');
    localStorage.removeItem('admin_permissions');
    adminApi.setToken(null);
  }

  return {
    user,
    token,
    adminRole,
    isSuperAdmin,
    permissions,
    loading,
    isAuthenticated,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    initialize,
    login,
    verify2FA,
    logout,
  };
});
